import { Eye } from "lucide-react";
import { ReactNode } from "react";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { cn } from "../../../../lib/utils";
import { Button } from "../../common/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../common/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../common/select";

interface FormSelectOption {
  key?: string | number;
  value: string | number;
  label?: string;
  text?: string;
}

interface FormSelectWithButtonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  required?: boolean;
  placeholder?: string;
  options: FormSelectOption[];
  errorMessage?: string;
  disabled?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  onChange?: (value: string) => void;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonTheme?: "green" | "dark" | "blue" | "red" | "grey" | "lightGreen";
  buttonOnClick: () => void;
  buttonClassName?: string;
  buttonDisabled?: boolean;
  buttonOutLine?: boolean;
  selectClassName?: string;
}

export function FormSelectWithButton<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  placeholder = "Select an option",
  options = [],
  errorMessage,
  disabled = false,
  rules,
  onChange,
  buttonTitle = "View",
  buttonIcon,
  buttonOnClick,
  buttonClassName,
  buttonDisabled,
  selectClassName
}: FormSelectWithButtonProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: required ? errorMessage || "This field is required" : false,
        ...rules
      }}
      render={({ field }: any) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="tw-text-destructive tw-ml-0.5">*</span>}
          </FormLabel>
          <div className="tw-flex tw-items-end tw-gap-2">
            <div className="tw-flex-1 tw-min-w-0">
              <Select
                onValueChange={value => {
                  field.onChange(value);
                  onChange?.(value);
                }}
                value={field.value ? String(field.value) : undefined}
                disabled={disabled}
              >
                <FormControl>
                  <SelectTrigger
                    className={cn(
                      "tw-w-full !tw-h-10 tw-px-2 tw-overflow-clip",
                      selectClassName
                    )}
                  >
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {options.map(option => (
                    <SelectItem key={option.key || option.value} value={String(option.value)}>
                      {option.label || option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={buttonOnClick}
              disabled={buttonDisabled}
              className={cn(
                "tw-h-10 tw-shrink-0 tw-gap-1.5 tw-px-4 tw-border-border tw-font-medium tw-text-foreground hover:tw-bg-muted hover:tw-text-foreground",
                buttonClassName
              )}
            >
              {buttonIcon ?? <Eye className="tw-h-4 tw-w-4" />}
              {buttonTitle}
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
